import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { propertyLabel } from '@/utils/createAppraisal';

export const DUPLICATE_ADDRESS_EVENT = 'rtr:duplicate-address';

// Global blocker shown whenever the backend refuses a duplicate property address.
export default function DuplicatePropertyDialog() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const h = (e) => setInfo(e.detail);
    window.addEventListener(DUPLICATE_ADDRESS_EVENT, h);
    return () => window.removeEventListener(DUPLICATE_ADDRESS_EVENT, h);
  }, []);

  const ex = info?.existing || {};
  const go = (path) => { setInfo(null); navigate(path); };
  return (
    <Dialog open={!!info} onOpenChange={(o) => { if (!o) setInfo(null); }}>
      <DialogContent className="max-w-md z-[80]" data-testid="duplicate-address-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" /> Address already on file</DialogTitle>
          <DialogDescription>Duplicate addresses are blocked. Fix it by using the existing property.</DialogDescription>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm" data-testid="duplicate-address-existing">
          <p className="font-semibold text-slate-800">{propertyLabel(ex)}{ex.property_address_city ? `, ${ex.property_address_city}` : ''}</p>
          <p className="text-xs text-slate-600 mt-0.5">{ex.property_no ? `P${ex.property_no}` : ''}{ex.client_name ? ` · owned by ${ex.client_name}` : ''}</p>
        </div>
        <p className="text-xs text-slate-500">
          If the property has changed hands, open it and use <b>Transfer Ownership</b> on its status card. Second roofs on the same property go under <b>Roof Details</b>, not a new property.
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <button data-testid="duplicate-address-close" onClick={() => setInfo(null)} className="px-3 py-1.5 text-sm font-semibold border border-slate-300 rounded hover:bg-slate-50">Close</button>
          {ex.client_id && (
            <button data-testid="duplicate-address-open-client" onClick={() => go(`/clients/${ex.client_id}`)} className="px-3 py-1.5 text-sm font-semibold border border-amber-400 text-amber-800 bg-amber-50 rounded hover:bg-amber-100">Open owner</button>
          )}
          <button data-testid="duplicate-address-open-property" onClick={() => go(`/properties/${ex._id}`)} className="px-3 py-1.5 text-sm font-semibold bg-[#D35400] hover:bg-[#A04000] text-white rounded">Open existing property</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
